import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Check, Point, Trophy } from '../../assets/images';
import DonutChart from '../charts/donutChart';
import { calculatePercentage } from '../../utils/base';
import { PaperCard } from '../card/PaperCard';
import ProgressBar from '../charts/progressBar';
import { getBirthToAge, calculateHeartRateZones, formatDateTimeDisplay } from '../../utils/base';
import ChallengeResult from '../challengeResult';
import { CHALLENGE_HEARTBEAT_MAX } from '../../constants';

// 공통 스타일 상수
const COMMON_STYLES = {
    cardHeight: '206px',
    titleFontSize: '18px',
    titleFontWeight: 500,
    titleLetterSpacing: '-0.54px',
    rewardFontSize: '14px',
    rewardFontWeight: 400,
    rewardLetterSpacing: '-0.42px',
    rewardBoxHeight: '30px',
    rewardBoxPadding: '5px 14px',
    rewardBoxGap: '5px',
    rewardBackgroundOpacity: 0.2,
    rewardBorderRadius: '10px',
};

// 배경색 선택 함수
const getBackgroundColor = (type, theme) => {
    if (type === 0) return theme.palette.figma.walkingChallengeBg;
    if (type === 1) return theme.palette.figma.heartRateChallengeBg;
    return theme.palette.figma.Red_didim;
};

// PaperCard 공통 스타일
const getCardStyles = (type, theme) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: COMMON_STYLES.cardHeight,
});

// 제목 Typography 공통 스타일
const getTitleStyles = (theme) => ({
    color: theme.palette.figma.buttonText,
    fontSize: COMMON_STYLES.titleFontSize,
    fontStyle: 'normal',
    fontWeight: COMMON_STYLES.titleFontWeight,
    lineHeight: 'normal',
    letterSpacing: COMMON_STYLES.titleLetterSpacing,
    textAlign: 'left',
});

// 보상 Typography 공통 스타일
const getRewardTextStyles = (theme) => ({
    color: theme.palette.figma.buttonText,
    textAlign: 'center',
    fontSize: COMMON_STYLES.rewardFontSize,
    fontStyle: 'normal',
    fontWeight: COMMON_STYLES.rewardFontWeight,
    lineHeight: 'normal',
    letterSpacing: COMMON_STYLES.rewardLetterSpacing,
});

// 보상 Box 공통 스타일
const getRewardBoxStyles = () => ({
    position: 'relative',
    display: 'flex',
    height: COMMON_STYLES.rewardBoxHeight,
    padding: COMMON_STYLES.rewardBoxPadding,
    justifyContent: 'center',
    alignItems: 'center',
    gap: COMMON_STYLES.rewardBoxGap,
    alignSelf: 'flex-start',
});

// 보상 배경 Box 공통 스타일
const getRewardBackgroundStyles = (theme) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: COMMON_STYLES.rewardBorderRadius,
    opacity: COMMON_STYLES.rewardBackgroundOpacity,
    background: theme.palette.figma.buttonText,
});

// 가운데 영역 공통 스타일
const getMiddleAreaStyles = () => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: '100%',
    minHeight: 0,
    paddingBottom: '10px',
});

// 제목 영역 공통 스타일
const getTitleAreaStyles = () => ({
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: '10px',
    position: 'relative',
    flexShrink: 0,
});

const isTargetZone = (index, value, heartRateZones) => {
    if (index === 0) {
        return value < heartRateZones.zone1;
    } else if (index === 1) {
        return value < heartRateZones.zone2;
    } else if (index === 2) {
        return value < heartRateZones.zone3;
    } else if (index === 3) {
        return value < heartRateZones.zone4;
    } else if (index === 4) {
        return value < heartRateZones.zone5;
    }
};

// HeartRateZoneLabel 공통 컴포넌트
const HeartRateZoneLabel = ({ theme, children, position = 'relative', left = 'auto', textAlign = 'right' }) => {
    return (
        <Typography
            sx={{
                position,
                left,
                color: theme.palette.figma.buttonText,
                fontSize: '12px',
                fontWeight: 400,
                letterSpacing: '-0.36px',
                textAlign,
                flex: 1,
            }}
        >
            {children}
        </Typography>
    );
};

const isLastDateValid = (lastDate, period) => {
    if (!lastDate) return false;
    if (period === 3) return true; // period가 3이면 무관

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const lastDateObj = new Date(lastDate);
    lastDateObj.setHours(0, 0, 0, 0);

    if (period === 0) {
        return lastDateObj.getTime() === today.getTime();
    } else if (period === 1) {
        const dayOfWeek = today.getDay();
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const monday = new Date(today);
        monday.setDate(today.getDate() - daysFromMonday);
        monday.setHours(0, 0, 0, 0);
        return lastDateObj >= monday && lastDateObj <= today;
    } else if (period === 2) {
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        firstDayOfMonth.setHours(0, 0, 0, 0);
        return lastDateObj >= firstDayOfMonth && lastDateObj <= today;
    }

    return false;
};

/** API `name`(예: physical_step_daily) → `challenge_name_<name>` 번역, 키 없으면 `nameKey` 문자열 */
function useChallengeTitle(nameKey) {
    const { t, i18n } = useTranslation();
    return useMemo(() => {
        if (!nameKey) {
            return '';
        }
        const k = `challenge_name_${nameKey}`;
        if (i18n.exists(k)) {
            return t(k);
        }
        return nameKey;
    }, [nameKey, i18n, t]);
}

// Challenge Item Component
export const ChallengeItem = ({
    type,
    nameKey,
    completedCount,
    completedRewardPoints,
    value,
    period,
    targetCount,
    targetValue,
    rewardPoint,
    lastDate,
    unit = '',
    onClick = () => { },
    user = null
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const challengeTitle = useChallengeTitle(nameKey);
    const color = theme.palette.figma.steps;

    const [meInfo, setMeInfo] = useState(JSON.parse(localStorage.getItem('meInfo')));
    const [validCompletedCount, setValidCompletedCount] = useState(completedCount);
    const [validValue, setValidValue] = useState(value);

    // 해당 유저의 나이 계산 (user prop이 있으면 user 사용, 없으면 meInfo 사용)
    const userInfo = user || meInfo;
    const age = getBirthToAge(userInfo?.birth);

    const heartRateZones = {
        zone1: calculateHeartRateZones(age, targetValue[0]),
        zone2: calculateHeartRateZones(age, targetValue[1]),
        zone3: calculateHeartRateZones(age, targetValue[2]),
        zone4: calculateHeartRateZones(age, targetValue[3]),
        zone5: calculateHeartRateZones(age, targetValue[4])
    };



    useEffect(() => {
        setMeInfo(JSON.parse(localStorage.getItem('meInfo')));

        if (isLastDateValid(lastDate, period)) {
            setValidCompletedCount(completedCount);
            setValidValue(value);
        } else {
            setValidCompletedCount(0);
            setValidValue(0);
        }
    }, [lastDate, period, completedCount, value]);

    return (
        <PaperCard
            background={getBackgroundColor(type, theme)}
            onClick={onClick}
            contentSx={getCardStyles(type, theme)}
            sx={getCardStyles(type, theme)}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', maxWidth: '100%', margin: '0 auto', position: 'relative', height: '100%', justifyContent: 'flex-start' }}>
                {/* 제목 영역 (고정) */}
                <Box
                    sx={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'flex-start',
                        alignItems: 'flex-start',
                        gap: '10px',
                        p: '0px',
                        m: '0px',
                        position: 'relative',
                        flex: 1,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '10px',
                            width: '100%',
                            maxWidth: '100%',
                        }}
                    >
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            width: '100%',
                            gap: '10px',
                        }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                    gap: '5px',
                                }}
                            >
                                {/* 완료 체크 아이콘 */}
                                {(type === 0 || (type === 2 && period !== 3)) && validValue >= targetValue && <Check />}
                                <Typography sx={getTitleStyles(theme)}>
                                    {challengeTitle}
                                </Typography>
                            </Box>
                            {type === 2 && period === 3 && <Typography
                                sx={{
                                    color: theme.palette.figma.buttonText,
                                    fontSize: '14px',
                                    fontStyle: 'normal',
                                    fontWeight: 500,
                                    lineHeight: 'normal',
                                    letterSpacing: '-0.42px',
                                    opacity: 0.8,
                                }}
                            >
                                {t('value_didim_per_day', { value: targetValue })}
                            </Typography>}
                        </Box>
                        {(type === 0 || (type === 2 && period === 0)) ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    width: '100%',
                                    height: '65px',
                                    flexDirection: 'column',
                                    justifyContent: 'flex-start',
                                    alignItems: 'flex-start',
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: theme.palette.figma.buttonText,
                                        textAlign: 'center',
                                        fontSize: '40px',
                                        fontStyle: 'normal',
                                        fontWeight: 600,
                                        lineHeight: 'normal',
                                        letterSpacing: '-1.2px',
                                    }}
                                >
                                    {validValue?.toLocaleString()}
                                </Typography>
                                <Typography
                                    sx={{
                                        color: theme.palette.figma.buttonText,
                                        textAlign: 'center',
                                        fontSize: '14px',
                                        fontStyle: 'normal',
                                        fontWeight: 400,
                                        lineHeight: 'normal',
                                        letterSpacing: '-0.42px',
                                    }}
                                >
                                    /{' '}
                                    {targetValue?.toLocaleString() + ' ' + unit}
                                </Typography>
                            </Box>
                        ) : (type === 1 || (type === 3 && period === 0)) ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    padding: '10px 0',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '5px',
                                    alignSelf: 'center'
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'relative',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        alignSelf: 'stretch',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: theme.palette.figma.buttonText,
                                            fontSize: '12px',
                                            fontWeight: 400,
                                            letterSpacing: '-0.36px',
                                            textAlign: 'right',
                                            flex: 1,
                                        }}
                                    >
                                        {heartRateZones.zone2}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: theme.palette.figma.buttonText,
                                            fontSize: '12px',
                                            fontWeight: 400,
                                            letterSpacing: '-0.36px',
                                            textAlign: 'right',
                                            flex: 1,
                                        }}
                                    >
                                        {heartRateZones.zone3}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: theme.palette.figma.buttonText,
                                            fontSize: '12px',
                                            fontWeight: 400,
                                            letterSpacing: '-0.36px',
                                            textAlign: 'right',
                                            flex: 1,
                                        }}
                                    >
                                        {heartRateZones.zone4}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: theme.palette.figma.buttonText,
                                            fontSize: '12px',
                                            fontWeight: 400,
                                            letterSpacing: '-0.36px',
                                            textAlign: 'right',
                                            flex: 1,
                                        }}
                                    >
                                        {heartRateZones.zone5}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: theme.palette.figma.buttonText,
                                            fontSize: '12px',
                                            fontWeight: 400,
                                            letterSpacing: '-0.36px',
                                            textAlign: 'right',
                                            flex: 1,
                                        }}
                                    >
                                        { }
                                    </Typography>
                                    <HeartRateZoneLabel
                                        theme={theme}
                                        position="absolute"
                                        left={0}
                                        textAlign="left"
                                    >
                                        {heartRateZones.zone1}
                                    </HeartRateZoneLabel>
                                </Box>

                                {/* 프로그레스바 */}
                                <Box
                                    sx={{
                                        position: 'relative',
                                        width: '300px',
                                        height: '30px',
                                        borderRadius: '32px',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {/* 배경 */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: 0,
                                            right: 0,
                                            width: '100%',
                                            height: '30px',
                                            backgroundColor: theme.palette.figma.black,
                                            opacity: 0.3,
                                            borderRadius: '32px',
                                            transform: 'translateY(-50%)',
                                        }}
                                    />
                                    <Box sx={{
                                        width: '300px',
                                        height: '55px',
                                        flexShrink: 0,
                                        borderRadius: '15px',
                                        opacity: 0.3,
                                        background: theme.palette.figma.black,
                                    }}>
                                    </Box>

                                    {/* 진행률 */}
                                    {(() => {
                                        // zone 레이블이 5개 구간으로 균등 분배되어 있으므로, 프로그레스 바도 동일하게 계산
                                        // zone1: 0% ~ 20%, zone2: 20% ~ 40%, zone3: 40% ~ 60%, zone4: 60% ~ 80%, zone5: 80% ~ 100%
                                        const numValue = Number(validValue);
                                        const zone1 = Number(heartRateZones.zone1);
                                        const zone2 = Number(heartRateZones.zone2);
                                        const zone3 = Number(heartRateZones.zone3);
                                        const zone4 = Number(heartRateZones.zone4);
                                        const zone5 = Number(heartRateZones.zone5);

                                        // 최고심박 계산 (zone5는 최고심박의 90%, 최고심박의 100%까지가 zone5 구간)
                                        const maxHeartRate = CHALLENGE_HEARTBEAT_MAX - age; // 최고심박 = CHALLENGE_HEARTBEAT_MAX - age
                                        const zone5End = maxHeartRate; // zone5 끝 = 최고심박의 100%

                                        // 각 zone의 시작 퍼센트 (5개 구간으로 균등 분배)
                                        const zone1StartPercent = 0;
                                        const zone2StartPercent = 20;
                                        const zone3StartPercent = 40;
                                        const zone4StartPercent = 60;
                                        const zone5StartPercent = 80;
                                        const zoneWidthPercent = 20; // 각 zone은 20%씩 차지

                                        let progressWidth = 0;

                                        if (numValue < zone1) {
                                            progressWidth = 0;
                                        } else if (numValue >= zone5End) {
                                            // 최고심박의 100%를 넘으면 100% 표시
                                            progressWidth = 100;
                                        } else {
                                            // value가 어느 zone에 있는지 확인하고 해당 zone 내에서의 비율 계산
                                            if (numValue >= zone1 && numValue < zone2) {
                                                // zone1 구간 (0% ~ 20%)
                                                const zoneRange = zone2 - zone1;
                                                const zoneProgress = zoneRange > 0 ? ((numValue - zone1) / zoneRange) : 0;
                                                progressWidth = zone1StartPercent + (zoneWidthPercent * zoneProgress);
                                            } else if (numValue >= zone2 && numValue < zone3) {
                                                // zone2 구간 (20% ~ 40%)
                                                const zoneRange = zone3 - zone2;
                                                const zoneProgress = zoneRange > 0 ? ((numValue - zone2) / zoneRange) : 0;
                                                progressWidth = zone2StartPercent + (zoneWidthPercent * zoneProgress);
                                            } else if (numValue >= zone3 && numValue < zone4) {
                                                // zone3 구간 (40% ~ 60%)
                                                const zoneRange = zone4 - zone3;
                                                const zoneProgress = zoneRange > 0 ? ((numValue - zone3) / zoneRange) : 0;
                                                progressWidth = zone3StartPercent + (zoneWidthPercent * zoneProgress);
                                            } else if (numValue >= zone4 && numValue < zone5) {
                                                // zone4 구간 (60% ~ 80%)
                                                const zoneRange = zone5 - zone4;
                                                const zoneProgress = zoneRange > 0 ? ((numValue - zone4) / zoneRange) : 0;
                                                progressWidth = zone4StartPercent + (zoneWidthPercent * zoneProgress);
                                            } else if (numValue >= zone5 && numValue < zone5End) {
                                                // zone5 구간 (80% ~ 100%)
                                                // zone5는 최고심박의 90%~100% 구간
                                                const zoneRange = zone5End - zone5;
                                                const zoneProgress = zoneRange > 0 ? ((numValue - zone5) / zoneRange) : 0;
                                                progressWidth = zone5StartPercent + (zoneWidthPercent * zoneProgress);
                                            }
                                        }

                                        // zone1 구간의 최대 비율 (20%)
                                        const zone1ToZone2MaxPercent = 20;

                                        // borderRadius 문제 해결을 위한 최소 width 계산
                                        const borderRadius = 32;
                                        const minWidthPx = borderRadius * 2; // 64px                                        

                                        // zone1 구간인지 확인
                                        const isZone1Range = numValue >= zone1 && numValue < zone2;

                                        // finalWidth 계산: zone1 구간일 때는 zone2를 절대 넘지 않도록 제한
                                        let finalWidth = progressWidth;
                                        let shouldApplyMinWidth = false;

                                        if (isZone1Range) {
                                            // zone1 구간일 때는 20%를 절대 넘지 않도록 제한
                                            finalWidth = Math.min(progressWidth, zone1ToZone2MaxPercent);

                                            // value가 zone1과 같을 때 (도달한 상태) 아주 약간이라도 보이게 하기
                                            if (numValue === zone1 && finalWidth === 0) {
                                                finalWidth = 1; // 1% 표시
                                            }
                                        } else {
                                            // zone1 구간이 아닐 때는 progressWidth 그대로 사용
                                            finalWidth = Math.min(100, progressWidth);
                                        }

                                        // 프로그레스 바의 borderRadius: value가 작을 때는 왼쪽만, 클 때는 양쪽 모두
                                        const progressBorderRadius = finalWidth >= 100
                                            ? '32px'
                                            : '32px 0 0 32px'; // 왼쪽만 둥글게

                                        // 최대심박수의 50% 계산
                                        const maxHeartRate50Percent = maxHeartRate * 0.5;
                                        // 숫자가 들어갈 수 있는 최소 너비 (대략 60px, 약 20%)
                                        const minWidthForText = 20; // 퍼센트

                                        // 게이지바 안에 숫자가 들어갈 수 있는지 확인
                                        // 조건: 1) 최대심박수의 50%를 넘어야 함, 2) 게이지바 너비가 충분해야 함
                                        const canFitInGauge = numValue > maxHeartRate50Percent && finalWidth >= minWidthForText;
                                        // 왼쪽에 표시해야 하는지 확인 (최대심박수의 50%를 넘지만 게이지바가 작을 때)
                                        const shouldShowValueOnLeft = numValue > maxHeartRate50Percent && finalWidth < minWidthForText;

                                        return (
                                            <>
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: '50%',
                                                        left: 0,
                                                        width: `${finalWidth}%`,
                                                        minWidth: shouldApplyMinWidth ? `${minWidthPx}px` : 'auto',
                                                        height: '30px',
                                                        backgroundColor: type === 1 ? theme.palette.figma.heartRate : theme.palette.figma.didim,
                                                        borderRadius: progressBorderRadius,
                                                        transform: 'translateY(-50%)',
                                                    }}
                                                >
                                                    {/* 현재 심박수 표시 (게이지바 안에 들어갈 수 있을 때만 우측 정렬) */}
                                                    {canFitInGauge && (
                                                        <Typography
                                                            sx={{
                                                                position: 'absolute',
                                                                right: '10px',
                                                                top: '50%',
                                                                transform: 'translateY(-50%)',
                                                                color: theme.palette.figma.buttonText,
                                                                fontSize: '16px',
                                                                fontWeight: 700,
                                                                letterSpacing: '-0.48px',
                                                                textAlign: 'right',
                                                            }}
                                                        >
                                                            {validValue}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                {/* 백그라운드 왼쪽에 심박수 표시 (최대심박수의 50%를 넘지만 게이지바가 작을 때) */}
                                                {shouldShowValueOnLeft && (
                                                    <Typography
                                                        sx={{
                                                            position: 'absolute',
                                                            left: '10px',
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            color: theme.palette.figma.buttonText,
                                                            fontSize: '16px',
                                                            fontWeight: 700,
                                                            letterSpacing: '-0.48px',
                                                            textAlign: 'left',
                                                            zIndex: 1,
                                                        }}
                                                    >
                                                        {validValue}
                                                    </Typography>
                                                )}
                                            </>
                                        );
                                    })()}
                                </Box>

                                {/* 포인트 레벨 표시 */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        height: '12px',
                                        justifyContent: 'center',
                                        alignItems: 'flex-end',
                                        alignSelf: 'stretch',
                                    }}
                                >
                                    {['5P', '15P', '25P', '35P', '50P'].map((point, index) => (
                                        <React.Fragment key={index}>
                                            <Typography
                                                sx={{
                                                    color: theme.palette.figma.iconWhite,
                                                    fontSize: '12px',
                                                    fontWeight: isTargetZone(index, validValue, heartRateZones) ? 600 : 400,
                                                    letterSpacing: '-0.36px',
                                                    textAlign: 'center',
                                                    opacity: isTargetZone(index, validValue, heartRateZones) ? 0.6 : 1,
                                                    flex: 1,
                                                }}
                                            >
                                                {point}
                                            </Typography>
                                            {
                                                index < 4 && (
                                                    <Box
                                                        sx={{
                                                            width: '1px',
                                                            height: '47px',
                                                            backgroundColor: theme.palette.figma.iconWhite,
                                                            opacity: 0.3,
                                                        }}
                                                    />
                                                )
                                            }
                                        </React.Fragment>
                                    ))}

                                </Box>
                            </Box>
                        ) : <Box sx={{
                            display: 'flex',
                            padding: '5px 0',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '5px',
                            alignSelf: 'stretch',
                        }}>
                            <ProgressBar
                                rangeStart={targetCount[0]}
                                rangeEnd={targetCount[1]}
                                start={targetCount[0]}
                                end={validCompletedCount}
                                height="30px"
                                showCurrentIndicator={false}
                                showProgress={true}
                                startLabel={targetCount[0] + t('day')}
                                endLabel={targetCount[1] + t('day')}
                                showStartEndValues={true}
                                showValueLabel={true}
                                valueLabelPosition="right"
                                valueLabel={validCompletedCount + t('day')}
                                backgroundColor={theme.palette.figma.white[20]}
                                progressColor={theme.palette.figma.steps}
                                borderRadius="23px"
                                startEndLabelSx={{
                                    color: theme.palette.figma.buttonText,
                                    textAlign: 'right',
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    letterSpacing: '-0.42px',
                                    opacity: 0.8,
                                }}
                                sx={{ width: '300px' }}
                            />
                        </Box>}
                    </Box>

                    <Box
                        sx={{
                            position: 'absolute',
                            right: '0px',
                            width: '80px',
                            height: '80px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flex: 1,
                        }}
                    >
                        {(type === 0 || (type === 2 && period === 0)) && (
                            // 걷기 챌린지 - 도넛 차트
                            <Box sx={{ position: 'relative' }}>
                                <DonutChart
                                    data={[
                                        {
                                            label: challengeTitle.split(' ')[0],
                                            value: calculatePercentage(
                                                validValue,
                                                targetValue
                                            ),
                                            color: color,
                                            radius: 30,
                                            strokeWidth: 10,
                                        },
                                    ]}
                                    size={70}
                                    centerRadius={35}
                                />
                                <Typography
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        fontSize: '14px',
                                        color: theme.palette.figma.buttonText,
                                        textAlign: 'center',
                                        fontWeight: 600,
                                        letterSpacing: '-0.42px',
                                        fontStyle: 'normal',
                                        lineHeight: 'normal',
                                    }}
                                >
                                    {Math.floor((validValue / targetValue) * 100)}%
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Reward Section (고정) */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: type === 2 && period === 3 ? 'space-between' : 'flex-start',
                    alignItems: 'center',
                    alignSelf: 'stretch',

                }}>
                    {type === 2 && period === 3 && <Box
                        sx={{
                            display: 'flex',
                            height: '30px',
                            padding: '5px 0',
                            alignItems: 'center',
                            gap: '5px',
                        }}
                    >
                        <Typography
                            sx={{
                                color: theme.palette.figma.buttonText,
                                textAlign: 'center',
                                fontSize: '14px',
                                fontStyle: 'normal',
                                fontWeight: 400,
                                lineHeight: 'normal',
                                letterSpacing: '-0.42px',
                            }}
                        >
                            {t('reward_received')}
                        </Typography>
                        <Point />
                        <Typography
                            sx={{
                                color: theme.palette.figma.buttonText,
                                textAlign: 'center',
                                fontSize: '14px',
                                fontStyle: 'normal',
                                fontWeight: 400,
                                lineHeight: 'normal',
                                letterSpacing: '-0.42px',
                            }}
                        >
                            {completedRewardPoints && completedRewardPoints.length > 0
                                ? completedRewardPoints[completedRewardPoints.length - 1]
                                : 0}
                        </Typography>
                    </Box>}
                    <Box sx={getRewardBoxStyles()}>
                        <Typography sx={getRewardTextStyles(theme)}>
                            {type === 2 && period === 3 ? t('next_reward') : t('reward')}
                        </Typography>
                        <Point />
                        <Typography sx={getRewardTextStyles(theme)}>
                            {type === 0 || (type === 2 && period === 0) ? rewardPoint
                                : type === 1 || (type === 3 && period === 0) ? (
                                    // 달성한 보상: 현재 심박수가 해당 구간 이상이면 그 보상, 아니면 이전 구간의 보상
                                    validValue >= heartRateZones.zone5 ? rewardPoint[4]
                                        : validValue >= heartRateZones.zone4 ? rewardPoint[3]
                                            : validValue >= heartRateZones.zone3 ? rewardPoint[2]
                                                : validValue >= heartRateZones.zone2 ? rewardPoint[1]
                                                    : validValue >= heartRateZones.zone1 ? rewardPoint[0]
                                                        : 0
                                ) + ' ' + t('reached')
                                    : type === 2 && period === 3
                                        ? (completedRewardPoints && completedRewardPoints.length > 0 && rewardPoint && rewardPoint[completedRewardPoints.length] !== undefined
                                            ? rewardPoint[completedRewardPoints.length]
                                            : rewardPoint && rewardPoint[0] !== undefined ? rewardPoint[0] : 0)
                                        : rewardPoint[0]}
                        </Typography>
                        <Box sx={getRewardBackgroundStyles(theme)} />
                    </Box>
                </Box>
            </Box>
            {(type === 0 || (type === 2 && period !== 3)) && validValue >= targetValue && <ChallengeResult type="complete" />}
        </PaperCard >
    );
};

// Daily Count Challenge Component
export const DailyCountChallengeItem = ({
    type,
    period,
    nameKey,
    completedCount,
    completedDates,
    targetCount,
    targetValue,
    rewardPoint,
    lastDate,
    onClick = () => { }
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const challengeTitle = useChallengeTitle(nameKey);
    const days = [t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat'), t('sun')];
    const [meInfo, setMeInfo] = useState(JSON.parse(localStorage.getItem('meInfo')));
    const [validCompletedCount, setValidCompletedCount] = useState(completedCount);
    const [validCompletedDates, setValidCompletedDates] = useState(completedDates);

    useEffect(() => {
        setMeInfo(JSON.parse(localStorage.getItem('meInfo')));

        if (isLastDateValid(lastDate, period)) {
            setValidCompletedCount(completedCount);
            setValidCompletedDates(completedDates);
        } else {
            setValidCompletedCount(0);
            setValidCompletedDates([]);
        }
    }, [lastDate, period, completedCount, completedDates]);

    // 나이 계산 및 심박수 구간 계산
    const age = getBirthToAge(meInfo.birth);
    const heartRateZones = calculateHeartRateZones(age, targetValue);

    // 현재 요일 계산 (0: 월요일, 1: 화요일, ..., 6: 일요일)
    const getCurrentDayOfWeek = () => {
        const day = new Date().getDay();
        // 일요일(0)을 6으로, 월요일(1)을 0으로 변환
        return day === 0 ? 6 : day - 1;
    };

    const currentDayOfWeek = getCurrentDayOfWeek();

    // 챌린지 성공 가능성 판단
    const isChallengeFailed = () => {
        // 현재까지 완료한 횟수 (validCompletedDates 배열 길이)
        const currentCompleted = validCompletedDates ? validCompletedDates.length : 0;

        const today = new Date();
        const todayString = today.toISOString().split('T')[0];

        // 오늘을 이미 완료했는지 확인
        const isTodayCompleted = validCompletedDates && validCompletedDates.includes(todayString);

        if (period === 1) {
            // 주간 챌린지: 주 단위로 판단
            // 남은 일수 계산 (현재 요일부터 일요일까지)
            const remainingDays = 7 - currentDayOfWeek;

            // 오늘을 완료했다면 오늘도 남은 일수에 포함
            const actualRemainingDays = isTodayCompleted ? remainingDays - 1 : remainingDays;

            // 목표까지 필요한 횟수
            const neededCount = targetCount - currentCompleted;

            // 남은 일수로 목표를 달성할 수 없는 경우 실패
            const failed = neededCount > actualRemainingDays;

            return failed;
        } else if (period === 2) {
            // 월간 챌린지: 이번 달 남은 날짜로 판단

            const currentYear = today.getFullYear();
            const currentMonth = today.getMonth();

            // 이번 달의 마지막 날 계산
            const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

            // 오늘부터 이번 달 마지막 날까지 남은 일수
            const remainingDaysInMonth = lastDayOfMonth - today.getDate() + 1;

            // 오늘을 완료했다면 오늘도 남은 일수에 포함
            const actualRemainingDaysInMonth = isTodayCompleted ? remainingDaysInMonth - 1 : remainingDaysInMonth;

            // 목표까지 필요한 횟수
            const neededCount = targetCount - currentCompleted;

            // 남은 일수로 목표를 달성할 수 없는 경우 실패
            const failed = neededCount > actualRemainingDaysInMonth;

            return failed;
        } else {
            // 기타 챌린지: 전체 기간으로 판단
            // 목표까지 필요한 횟수
            const neededCount = targetCount - currentCompleted;

            // 목표를 달성할 수 없는 경우 실패 (현재 완료 횟수가 목표보다 적으면 실패)
            const failed = neededCount > 0 && currentCompleted >= targetCount ? false : neededCount > 0;
            return failed;
        }
    };

    // 날짜 상태 계산
    const getDayStatus = index => {
        // 현재 주의 시작일 (월요일) 계산
        const today = new Date();
        const currentWeekStart = new Date(today);
        // 월요일(1)을 기준으로 주의 시작일 계산
        const dayOfWeek = today.getDay();
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        currentWeekStart.setDate(today.getDate() - daysFromMonday);

        // 해당 인덱스의 날짜 계산
        const targetDate = new Date(currentWeekStart);
        targetDate.setDate(currentWeekStart.getDate() + index);

        // 날짜를 YYYY-MM-DD 형식으로 변환
        const targetDateString = targetDate.toISOString().split('T')[0];

        // 완료된 날짜 목록에 포함되어 있는지 확인
        if (validCompletedDates && validCompletedDates.includes(targetDateString)) {
            return 'completed'; // 완료된 날
        } else if (index < currentDayOfWeek) {
            return 'past'; // 과거 미완료 날
        } else {
            return 'future'; // 미래 날
        }
    };

    return (
        <PaperCard
            background={getBackgroundColor(type, theme)}
            onClick={onClick}
            contentSx={getCardStyles(type, theme)}
            sx={getCardStyles(type, theme)}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    height: '100%',
                    justifyContent: 'flex-start',

                }}
            >
                {/* 제목 영역 (고정) */}
                <Box sx={getTitleAreaStyles()}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '10px',
                            width: '100%',
                            maxWidth: '100%',
                        }}
                    >
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            gap: '10px',
                        }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                    gap: '5px',
                                }}
                            >       {/* 완료 체크 아이콘 */}
                                {validCompletedCount >= targetCount && <Check />}

                                {/* 제목 */}
                                <Typography
                                    sx={{
                                        ...getTitleStyles(theme),
                                        alignSelf: 'flex-start',
                                    }}
                                >
                                    {challengeTitle}
                                </Typography>
                            </Box>
                            <Typography
                                sx={{
                                    color: theme.palette.figma.buttonText,
                                    fontSize: '14px',
                                    fontStyle: 'normal',
                                    fontWeight: 500,
                                    lineHeight: 'normal',
                                    letterSpacing: '-0.42px',
                                    opacity: 0.8,
                                }}
                            >
                                {type === 2 ? t('value_didim_per_day', { value: targetValue[0] }) : type === 3 ? t('max_value_or_above', { value: heartRateZones }) : ''}
                            </Typography>
                        </Box>
                        {/* 원형 인디케이터 */}
                        {period === 1 ? < Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                width: '100%',
                                padding: '0 0px',
                            }}
                        >
                            {
                                days.map((day, index) => {
                                    const dayStatus = getDayStatus(index);

                                    return (
                                        <Box
                                            key={index}
                                            sx={{
                                                position: 'relative',
                                                width: '34px',
                                                height: '34px',
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {/* 배경 이미지/색상 */}
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    width: '34px',
                                                    height: '34px',
                                                    borderRadius: '50%',
                                                    backgroundColor:
                                                        dayStatus === 'completed'
                                                            ? theme.palette.figma.steps
                                                            : dayStatus === 'past'
                                                                ? theme.palette.figma
                                                                    .iconWhite
                                                                : 'transparent',
                                                    border:
                                                        dayStatus === 'completed'
                                                            ? 'none'
                                                            : dayStatus === 'future'
                                                                ? `1px solid ${theme.palette.figma.iconWhite}`
                                                                : 'none',
                                                    opacity:
                                                        dayStatus === 'completed'
                                                            ? 1
                                                            : dayStatus === 'past'
                                                                ? 0.2
                                                                : 0.3,
                                                }}
                                            />

                                            {/* 텍스트 */}
                                            <Typography
                                                sx={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    color: theme.palette.figma.buttonText,
                                                    fontSize: '12px',
                                                    fontWeight:
                                                        dayStatus === 'completed'
                                                            ? 600
                                                            : 400,
                                                    opacity:
                                                        dayStatus === 'completed'
                                                            ? 1
                                                            : dayStatus === 'past'
                                                                ? 0.5
                                                                : 0.6,
                                                    letterSpacing: '-0.36px',
                                                    textAlign: 'center',
                                                    lineHeight: 'normal',
                                                }}
                                            >
                                                {day}
                                            </Typography>
                                        </Box>
                                    );
                                })
                            }
                        </Box> :
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    width: '100%',
                                    gap: '8px',
                                }}
                            >
                                {/* 첫 번째 줄 (1~6번째 완료 날짜) */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        width: '100%',
                                        gap: '8px',
                                    }}
                                >
                                    {Array.from({ length: 6 }, (_, index) => {
                                        const completedDate = validCompletedDates && validCompletedDates[index];
                                        const isCompleted = !!completedDate;

                                        // 날짜를 로케일별 월/일 표기로 변환하거나 회차 표시
                                        let displayText = '';
                                        if (completedDate) {
                                            displayText = formatDateTimeDisplay(completedDate, 'monthDay');
                                        } else {
                                            displayText = `${index + 1}${t('round')}`;
                                        }

                                        return (
                                            <Box
                                                key={index}
                                                sx={{
                                                    position: 'relative',
                                                    width: '40px',
                                                    height: '40px',
                                                    borderRadius: '50%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                {/* 배경 이미지/색상 */}
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: '50%',
                                                        left: '50%',
                                                        transform: 'translate(-50%, -50%)',
                                                        width: '40px',
                                                        height: '40px',
                                                        borderRadius: '50%',
                                                        backgroundColor: isCompleted
                                                            ? type === 2 ? theme.palette.figma.steps : theme.palette.figma.didim
                                                            : 'transparent',
                                                        border: isCompleted
                                                            ? 'none'
                                                            : `1px solid ${theme.palette.figma.iconWhite}`,
                                                        opacity: isCompleted ? 1 : 0.3,
                                                    }}
                                                />

                                                {/* 텍스트 */}
                                                <Typography
                                                    sx={{
                                                        position: 'absolute',
                                                        top: '50%',
                                                        left: '50%',
                                                        transform: 'translate(-50%, -50%)',
                                                        color: theme.palette.figma.buttonText,
                                                        fontSize: '11px',
                                                        fontWeight: isCompleted ? 600 : 400,
                                                        letterSpacing: '-0.36px',
                                                        textAlign: 'center',
                                                        lineHeight: 'normal',
                                                    }}
                                                >
                                                    {displayText}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>

                                {/* 두 번째 줄 (7~12번째 완료 날짜) */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        width: '100%',
                                        gap: '8px',
                                    }}
                                >
                                    {Array.from({ length: 6 }, (_, index) => {
                                        const completedDate = validCompletedDates && validCompletedDates[index + 6];
                                        const isCompleted = !!completedDate;

                                        // 날짜를 로케일별 월/일 표기로 변환하거나 회차 표시
                                        let displayText = '';
                                        let isTwelfth = false;
                                        if (completedDate) {
                                            displayText = formatDateTimeDisplay(completedDate, 'monthDay');
                                        } else {
                                            const count = index + 7;
                                            if (count === 12) {
                                                isTwelfth = true;
                                            } else {
                                                displayText = `${count}${t('round')}`;
                                            }
                                        }

                                        return (
                                            <Box
                                                key={index + 6}
                                                sx={{
                                                    position: 'relative',
                                                    width: '40px',
                                                    height: '40px',
                                                    borderRadius: '50%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                {/* 배경 이미지/색상 */}
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: '50%',
                                                        left: '50%',
                                                        transform: 'translate(-50%, -50%)',
                                                        width: '40px',
                                                        height: '40px',
                                                        borderRadius: '50%',
                                                        backgroundColor: isCompleted
                                                            ? type === 2 ? theme.palette.figma.steps : theme.palette.figma.didim
                                                            : 'transparent',
                                                        border: isCompleted
                                                            ? 'none'
                                                            : `1px solid ${theme.palette.figma.iconWhite}`,
                                                        opacity: isCompleted ? 1 : 0.3,
                                                    }}
                                                />

                                                {/* 텍스트 또는 아이콘 */}
                                                {isTwelfth && !isCompleted ? (
                                                    <Trophy
                                                        size={20}
                                                        color={theme.palette.figma.iconWhite}
                                                    />
                                                ) : (
                                                    <Typography
                                                        sx={{
                                                            position: 'absolute',
                                                            top: '50%',
                                                            left: '50%',
                                                            transform: 'translate(-50%, -50%)',
                                                            color: theme.palette.figma.buttonText,
                                                            fontSize: '11px',
                                                            fontWeight: isCompleted ? 600 : 400,
                                                            letterSpacing: '-0.36px',
                                                            textAlign: 'center',
                                                            lineHeight: 'normal'
                                                        }}
                                                    >
                                                        {displayText}
                                                    </Typography>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>}
                    </Box>
                </Box>

                {/* 가운데 영역 (늘어나는 영역) */}
                <Box sx={getMiddleAreaStyles()}>
                </Box>

                {/* Reward Section (고정) */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    alignSelf: 'stretch',
                    flexShrink: 0,
                }}>

                    < Box
                        sx={{
                            ...getRewardBoxStyles(),
                            width: '112px',
                            padding: '5px 0',
                        }}
                    >
                        <Typography sx={getRewardTextStyles(theme)}>
                            {t('reward')}
                        </Typography>
                        <Point />
                        <Typography sx={getRewardTextStyles(theme)}>
                            {rewardPoint}
                        </Typography>
                        <Box
                            sx={{
                                ...getRewardBackgroundStyles(theme),
                                width: '112px',
                                height: COMMON_STYLES.rewardBoxHeight,
                            }}
                        />
                    </Box>
                    {/* 진행률 표시 */}
                    < Typography
                        sx={{
                            color: theme.palette.figma.buttonText,
                            fontSize: '14px',
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: 'normal',
                            letterSpacing: '-0.42px',
                            textAlign: 'center',
                        }}
                    >
                        {t('valid_success_target_rounds', { valid: validCompletedCount, target: targetCount })}
                    </Typography >
                </Box>

                {/* 챌린지 결과 오버레이 */}
                {
                    (type === 0 || type === 2) && isChallengeFailed() ? (
                        <ChallengeResult type="fail" />
                    ) : (type === 0 || type === 2) && validCompletedCount >= targetCount && (
                        <ChallengeResult type="complete" />
                    )
                }
            </Box>
        </PaperCard >


    );
};

export default ChallengeItem;
